import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { PestModelsApiService } from '@app/core/pest-models-api.service';
import { PestModelsSubscriptionService } from '@app/core/pest-models-subscription.service';
import { PestMagnitudeConfigService } from '@app/core/pest-magnitude-config.service';
import {
  PestModelCatalogModel,
  PestModelCatalogParams,
  PestModelCatalogResponse,
  PestModelInsight,
  PestModelState
} from '@models/pest-models';
import { forkJoin, of, Subscription } from 'rxjs';
import { catchError, finalize, map, switchMap, take } from 'rxjs/operators';
import { FincaService } from '@app/core/finca.service';
import { UserService } from '@app/core/user.service';
import { SpinnerService } from '@app/shared/spinner';
import { TranslateService } from '@ngx-translate/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { HttpCriteria, SuperHttpClientProvider } from '@app/core/http';
import { ModuloService } from '@app/core/modulo.service';
import {
  AemetStationsService,
  AemetStation
} from '@app/core/aemet-stations.service';
import {
  SiarStationsService,
  SiarProvince,
  SiarStation
} from '@app/core/siar-stations.service';

type PestMagnitudeKey = 'temperature' | 'humidity' | 'precipitation' | 'wind';

interface PestMagnitudeConfigForm {
  temperatureSource: string | null;
  humiditySource: string | null;
  precipitationSource: string | null;
  windSource: string | null;
  temperatureSensorId: number | null;
  humiditySensorId: number | null;
  precipitationSensorId: number | null;
  windSensorId: number | null;
  temperatureAemetStationId: string | null;
  temperatureSiarStationId: string | null;
  humidityAemetStationId: string | null;
  humiditySiarStationId: string | null;
  precipitationAemetStationId: string | null;
  precipitationSiarStationId: string | null;
  windAemetStationId: string | null;
  windSiarStationId: string | null;
}

interface SourceOptionItem {
  value: string;
  labelKey: string;
  disabled?: boolean;
}

interface SensorOptionItem {
  id: number;
  label: string;
}

@Component({
  standalone: false,
  selector: 'app-pest-models',
  templateUrl: './pest-models.component.html',
  styleUrls: ['./pest-models.component.scss']
})
export class PestModelsComponent implements OnInit, OnDestroy {
  public fincaId: number | null = null;
  public catalog: PestModelCatalogResponse | null = null;
  public models: PestModelCatalogModel[] = [];
  public fincas: Array<{ idFinca: number; name: string }> = [];
  public modules: any[] = [];
  public selectedModuleId: number | null = null;
  public moduleMap = new Map<number, any>();
  public sensors: any[] = [];

  public includeState = true;
  public includeInsights = true;
  public onlyActive = false;
  public limit = 20;

  public loading = false;
  public fincasLoading = false;
  public modulesLoading = false;
  public sensorsLoading = false;
  public configLoading = false;
  public configSaving = false;
  public error = '';
  public roleId: number | null = null;
  public debugEnabled = false;
  public subscriptionModel: PestModelCatalogModel | null = null;
  public subscriptionLevel = 'WARN_ALERT';
  public subscriptionLevels = [
    { value: 'ALL', labelKey: 'PEST_MODELS.SUBSCRIPTION_LEVEL.ALL' },
    {
      value: 'WARN_ALERT',
      labelKey: 'PEST_MODELS.SUBSCRIPTION_LEVEL.WARN_ALERT'
    },
    {
      value: 'ALERT_ONLY',
      labelKey: 'PEST_MODELS.SUBSCRIPTION_LEVEL.ALERT_ONLY'
    },
    { value: 'NONE', labelKey: 'PEST_MODELS.SUBSCRIPTION_LEVEL.NONE' }
  ];

  private paramSub?: Subscription;
  private readonly payloadExpanded = new Set<string>();
  private readonly debugStorageKeyBase = 'pestModels.debugEnabled';
  @ViewChild('subscriptionDialog') subscriptionDialog!: TemplateRef<any>;
  private subscriptionDialogRef?: MatDialogRef<any>;
  public magnitudeConfig = this.buildEmptyMagnitudeConfig();
  public sourceOptions = [
    { value: 'SENSOR_FINCA', labelKey: 'PEST_MODELS.SOURCE.SENSOR_FINCA' },
    { value: 'OPEN_METEO', labelKey: 'PEST_MODELS.SOURCE.OPEN_METEO' },
    { value: 'AEMET', labelKey: 'PEST_MODELS.SOURCE.AEMET' },
    { value: 'SIAR', labelKey: 'PEST_MODELS.SOURCE.SIAR' },
    { value: 'NASA', labelKey: 'PEST_MODELS.SOURCE.NASA' }
  ];
  public precipitationSourceOptions = [
    { value: 'SENSOR_FINCA', labelKey: 'PEST_MODELS.SOURCE.SENSOR_FINCA' },
    { value: 'PLUVIOMETER', labelKey: 'PEST_MODELS.SOURCE.PLUVIOMETER' },
    { value: 'OPEN_METEO', labelKey: 'PEST_MODELS.SOURCE.OPEN_METEO' },
    { value: 'AEMET', labelKey: 'PEST_MODELS.SOURCE.AEMET' },
    { value: 'SIAR', labelKey: 'PEST_MODELS.SOURCE.SIAR' },
    { value: 'NASA', labelKey: 'PEST_MODELS.SOURCE.NASA' }
  ];
  public sourceOptionsByMagnitude: Record<PestMagnitudeKey, SourceOptionItem[]> =
    {
      temperature: [],
      humidity: [],
      precipitation: [],
      wind: []
    };
  public sensorOptionsByMagnitude: Record<PestMagnitudeKey, SensorOptionItem[]> =
    {
      temperature: [],
      humidity: [],
      precipitation: [],
      wind: []
    };
  public sensorAvailability: Record<PestMagnitudeKey, boolean> = {
    temperature: false,
    humidity: false,
    precipitation: false,
    wind: false
  };
  public showSensorInput: Record<PestMagnitudeKey, boolean> = {
    temperature: false,
    humidity: false,
    precipitation: false,
    wind: false
  };
  public showAemetInput: Record<PestMagnitudeKey, boolean> = {
    temperature: false,
    humidity: false,
    precipitation: false,
    wind: false
  };
  public showSiarInput: Record<PestMagnitudeKey, boolean> = {
    temperature: false,
    humidity: false,
    precipitation: false,
    wind: false
  };
  public aemetProvinces: string[] = [];
  public siarProvinces: SiarProvince[] = [];
  public aemetProvinceSelection: Record<PestMagnitudeKey, string | null> = {
    temperature: null,
    humidity: null,
    precipitation: null,
    wind: null
  };
  public siarProvinceSelection: Record<PestMagnitudeKey, string | number | null> =
    {
      temperature: null,
      humidity: null,
      precipitation: null,
      wind: null
    };
  public aemetStationsByMagnitude: Record<PestMagnitudeKey, AemetStation[]> =
    {
      temperature: [],
      humidity: [],
      precipitation: [],
      wind: []
    };
  public siarStationsByMagnitude: Record<PestMagnitudeKey, SiarStation[]> = {
    temperature: [],
    humidity: [],
    precipitation: [],
    wind: []
  };
  public aemetStationsLoading: Record<PestMagnitudeKey, boolean> = {
    temperature: false,
    humidity: false,
    precipitation: false,
    wind: false
  };
  public siarStationsLoading: Record<PestMagnitudeKey, boolean> = {
    temperature: false,
    humidity: false,
    precipitation: false,
    wind: false
  };

  constructor(
    private route: ActivatedRoute,
    private pestModelsApi: PestModelsApiService,
    private pestModelsSubscription: PestModelsSubscriptionService,
    private pestMagnitudeConfig: PestMagnitudeConfigService,
    private fincaService: FincaService,
    private moduloService: ModuloService,
    private $http: SuperHttpClientProvider,
    private userService: UserService,
    private spinner: SpinnerService,
    private translate: TranslateService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private aemetStationsService: AemetStationsService,
    private siarStationsService: SiarStationsService
  ) {}

  ngOnInit(): void {
    this.roleId = this.userService.getRoleId();
    if (this.roleId === null) {
      this.userService.roleId$.pipe(take(1)).subscribe(role => {
        this.roleId = role;
        this.loadDebugPreference();
        if (this.isNotAdmin()) {
          this.error = this.translate.instant('PEST_MODELS.ERROR_ADMIN_ONLY');
        }
      });
    } else if (this.isNotAdmin()) {
      this.loadDebugPreference();
      this.error = this.translate.instant('PEST_MODELS.ERROR_ADMIN_ONLY');
    } else {
      this.loadDebugPreference();
    }

    this.paramSub = this.route.paramMap.subscribe(params => {
      const idParam = params.get('idFinca');
      const parsed = idParam ? Number(idParam) : NaN;
      this.fincaId = Number.isFinite(parsed) ? parsed : null;
      this.refresh();
      this.loadInventoryForFinca();
    });
    this.loadFincas();
  }

  ngOnDestroy(): void {
    this.paramSub?.unsubscribe();
  }

  public refresh(): void {
    if (this.isNotAdmin()) {
      this.error = this.translate.instant('PEST_MODELS.ERROR_ADMIN_ONLY');
      this.models = [];
      return;
    }
    if (!this.fincaId) {
      this.error = this.translate.instant('PEST_MODELS.ERROR_NO_FARM_SELECTED');
      return;
    }
    const parsedLimit = Number(this.limit);
    const effectiveLimit =
      Number.isFinite(parsedLimit) && parsedLimit > 0 ? parsedLimit : 20;
    this.limit = effectiveLimit;
    const query: PestModelCatalogParams = {
      includeState: this.includeState,
      includeInsights: this.includeInsights,
      onlyActive: this.onlyActive,
      limit: effectiveLimit
    };

    this.loading = true;
    this.error = '';
    this.spinner.show();
    this.pestModelsApi
      .getCatalog(this.fincaId, query)
      .pipe(
        finalize(() => {
          this.loading = false;
          this.spinner.hide();
        })
      )
      .subscribe({
        next: catalog => {
          this.catalog = catalog;
          this.models = Array.isArray(catalog?.models) ? catalog.models : [];
        },
        error: () => {
          this.error = this.translate.instant('PEST_MODELS.ERROR_CATALOG_LOAD');
          this.models = [];
        }
      });
  }

  public onFincaChange(value: any): void {
    const parsed = Number(value);
    this.fincaId = Number.isFinite(parsed) ? parsed : null;
    this.refresh();
    this.loadInventoryForFinca();
  }

  private loadFincas(): void {
    if (this.isNotAdmin()) return;
    this.fincasLoading = true;
    this.fincaService
      .getFincas<Array<{ idFinca: number; name: string }>>()
      .pipe(
        take(1),
        finalize(() => {
          this.fincasLoading = false;
        })
      )
      .subscribe({
        next: data => {
          this.fincas = Array.isArray(data) ? data : [];
          if (!this.fincaId && this.fincas.length) {
            this.fincaId = this.fincas[0].idFinca;
            this.refresh();
            this.loadInventoryForFinca();
          }
        },
        error: () => {
          this.fincas = [];
        }
      });
  }

  private loadInventoryForFinca(): void {
    if (!this.fincaId) {
      this.modules = [];
      this.sensors = [];
      this.moduleMap.clear();
      this.selectedModuleId = null;
      this.magnitudeConfig = this.buildEmptyMagnitudeConfig();
      this.resetStationLists();
      return;
    }

    const criteria = new HttpCriteria({
      _filters: [
        {
          field: 'idFinca',
          operator: '=',
          value: this.fincaId
        }
      ],
      _perPage: -1
    });

    this.modulesLoading = true;
    this.moduloService
      .getModulos<any[]>(criteria)
      .pipe(
        take(1),
        catchError(() => of([])),
        switchMap(modules => {
          this.modules = Array.isArray(modules) ? modules : [];
          this.modules.sort((a, b) =>
            String(a?.name || '').localeCompare(String(b?.name || ''))
          );
          this.moduleMap.clear();
          this.modules.forEach(mod => {
            const id = this.getModuleId(mod);
            if (id) {
              this.moduleMap.set(id, mod);
            }
          });
          if (!this.modules.length) {
            this.selectedModuleId = null;
            this.sensors = [];
            this.magnitudeConfig = this.buildEmptyMagnitudeConfig();
            this.resetStationLists();
            return of([]);
          }
          this.selectedModuleId = this.getModuleId(this.modules[0]);
          return this.loadSensorsForModules(this.modules);
        }),
        finalize(() => {
          this.modulesLoading = false;
        })
      )
      .subscribe(sensors => {
        this.sensors = Array.isArray(sensors) ? sensors : [];
        this.updateSensorOptions();
        this.loadMagnitudeConfig();
      });
  }

  private loadSensorsForModules(modules: any[]): any {
    if (!modules.length) return of([]);
    const requests = modules.map(mod => {
      const moduleId = this.getModuleId(mod);
      if (!moduleId) return of([]);
      const criteria = new HttpCriteria({
        _filters: [
          {
            field: 'idModule',
            operator: '=',
            value: moduleId
          }
        ],
        _perPage: -1
      });
      return this.$http.default.filter('/admin/sensor', criteria).pipe(
        catchError(() => of([]))
      );
    });
    this.sensorsLoading = true;
    return forkJoin(requests).pipe(
      map(responses => responses.reduce((acc, cur) => acc.concat(cur), [])),
      finalize(() => {
        this.sensorsLoading = false;
      })
    );
  }

  public onModuleChange(value: any): void {
    const parsed = Number(value);
    this.selectedModuleId = Number.isFinite(parsed) ? parsed : null;
    this.loadMagnitudeConfig();
  }

  private loadMagnitudeConfig(): void {
    if (!this.selectedModuleId) {
      this.magnitudeConfig = this.buildEmptyMagnitudeConfig();
      return;
    }
    this.configLoading = true;
    this.pestMagnitudeConfig
      .getConfig<any>(this.selectedModuleId)
      .pipe(
        take(1),
        catchError(() => of(null)),
        finalize(() => {
          this.configLoading = false;
        })
      )
      .subscribe(config => {
        this.magnitudeConfig = {
          ...this.buildEmptyMagnitudeConfig(),
          ...(config || {})
        };
        this.syncSourceVisibility();
        this.ensureStationSelections();
      });
  }

  public saveMagnitudeConfig(): void {
    if (!this.selectedModuleId) return;
    const payload = this.buildMagnitudePayload();
    this.configSaving = true;
    this.pestMagnitudeConfig
      .saveConfig(this.selectedModuleId, payload)
      .pipe(
        finalize(() => {
          this.configSaving = false;
        })
      )
      .subscribe({
        next: () => {
          this.snackBar.open(
            this.translate.instant('PEST_MODELS.CONFIG_SAVE_SUCCESS'),
            this.translate.instant('COMMON.CLOSE'),
            { duration: 3500 }
          );
          this.loadMagnitudeConfig();
        },
        error: () => {
          this.snackBar.open(
            this.translate.instant('PEST_MODELS.CONFIG_SAVE_ERROR'),
            this.translate.instant('COMMON.CLOSE'),
            { duration: 4000 }
          );
        }
      });
  }

  public onSourceChange(magnitude: PestMagnitudeKey): void {
    const source = this.getSourceValue(magnitude);
    if (!this.isSensorSourceValue(magnitude, source)) {
      this.setSensorValue(magnitude, null);
    }
    if (source !== 'AEMET') {
      this.setStationValue(magnitude, 'aemet', null);
      this.aemetProvinceSelection[magnitude] = null;
      this.aemetStationsByMagnitude[magnitude] = [];
    }
    if (source !== 'SIAR') {
      this.setStationValue(magnitude, 'siar', null);
      this.siarProvinceSelection[magnitude] = null;
      this.siarStationsByMagnitude[magnitude] = [];
    }
    this.syncSourceVisibility();
    if (source === 'AEMET') {
      this.ensureAemetProvincesLoaded().pipe(take(1)).subscribe();
    }
    if (source === 'SIAR') {
      this.ensureSiarProvincesLoaded().pipe(take(1)).subscribe();
    }
  }

  public canSyncTempHumidity(): boolean {
    if (!this.showSensorInput.temperature) return false;
    if (!this.showSensorInput.humidity) return false;
    return (
      !!this.magnitudeConfig.temperatureSensorId ||
      !!this.magnitudeConfig.humiditySensorId
    );
  }

  public syncTempHumiditySensor(): void {
    if (!this.canSyncTempHumidity()) return;
    if (this.magnitudeConfig.temperatureSensorId && !this.magnitudeConfig.humiditySensorId) {
      this.magnitudeConfig.humiditySensorId = this.magnitudeConfig.temperatureSensorId;
      return;
    }
    if (this.magnitudeConfig.humiditySensorId && !this.magnitudeConfig.temperatureSensorId) {
      this.magnitudeConfig.temperatureSensorId = this.magnitudeConfig.humiditySensorId;
      return;
    }
    if (this.magnitudeConfig.temperatureSensorId) {
      this.magnitudeConfig.humiditySensorId = this.magnitudeConfig.temperatureSensorId;
    }
  }

  private updateSensorOptions(): void {
    const buildOptions = (magnitude: PestMagnitudeKey): SensorOptionItem[] => {
      const sensorTypes = this.getSensorTypesForMagnitude(magnitude);
      return (this.sensors || [])
        .filter(sensor => sensorTypes.includes(this.getSensorType(sensor)))
        .map(sensor => ({
          id: Number(sensor.idSensor),
          label: this.getSensorLabel(sensor)
        }))
        .filter(opt => Number.isFinite(opt.id));
    };

    this.sensorOptionsByMagnitude = {
      temperature: buildOptions('temperature'),
      humidity: buildOptions('humidity'),
      precipitation: buildOptions('precipitation'),
      wind: buildOptions('wind')
    };

    this.sensorAvailability = {
      temperature: this.sensorOptionsByMagnitude.temperature.length > 0,
      humidity: this.sensorOptionsByMagnitude.humidity.length > 0,
      precipitation: this.sensorOptionsByMagnitude.precipitation.length > 0,
      wind: this.sensorOptionsByMagnitude.wind.length > 0
    };

    this.sourceOptionsByMagnitude = {
      temperature: this.buildSourceOptions('temperature'),
      humidity: this.buildSourceOptions('humidity'),
      precipitation: this.buildSourceOptions('precipitation'),
      wind: this.buildSourceOptions('wind')
    };
  }

  private buildSourceOptions(
    magnitude: PestMagnitudeKey
  ): SourceOptionItem[] {
    const base =
      magnitude === 'precipitation'
        ? this.precipitationSourceOptions
        : this.sourceOptions;
    const hasSensors = this.sensorAvailability[magnitude];
    return base.map(option => {
      const isSensor =
        option.value === 'SENSOR_FINCA' ||
        (magnitude === 'precipitation' && option.value === 'PLUVIOMETER');
      return {
        ...option,
        disabled: isSensor ? !hasSensors : false
      };
    });
  }

  private syncSourceVisibility(): void {
    (['temperature', 'humidity', 'precipitation', 'wind'] as PestMagnitudeKey[])
      .forEach(magnitude => {
        const source = this.getSourceValue(magnitude);
        this.showSensorInput[magnitude] = this.isSensorSourceValue(
          magnitude,
          source
        );
        this.showAemetInput[magnitude] = source === 'AEMET';
        this.showSiarInput[magnitude] = source === 'SIAR';
      });
  }

  public onAemetProvinceChange(
    magnitude: PestMagnitudeKey,
    province: string | null
  ): void {
    this.aemetProvinceSelection[magnitude] = province || null;
    this.setStationValue(magnitude, 'aemet', null);
    this.aemetStationsByMagnitude[magnitude] = [];
    if (!province) return;
    this.loadAemetStations(magnitude, province);
  }

  public onAemetStationChange(
    magnitude: PestMagnitudeKey,
    stationId: string | null
  ): void {
    this.setStationValue(magnitude, 'aemet', stationId || null);
  }

  public onSiarProvinceChange(
    magnitude: PestMagnitudeKey,
    provinceId: string | number | null
  ): void {
    this.siarProvinceSelection[magnitude] = provinceId ?? null;
    this.setStationValue(magnitude, 'siar', null);
    this.siarStationsByMagnitude[magnitude] = [];
    if (provinceId === null || provinceId === undefined || provinceId === '') {
      return;
    }
    this.loadSiarStations(magnitude, provinceId);
  }

  public onSiarStationChange(
    magnitude: PestMagnitudeKey,
    stationId: string | number | null
  ): void {
    if (stationId === null || stationId === undefined || stationId === '') {
      this.setStationValue(magnitude, 'siar', null);
      return;
    }
    this.setStationValue(magnitude, 'siar', String(stationId));
  }

  public formatAemetStationLabel(station: AemetStation): string {
    if (!station) return '';
    const idema = station.idema ? String(station.idema) : '';
    const name = station.name ? String(station.name) : '';
    if (idema && name) {
      return `${idema} - ${name}`;
    }
    return idema || name;
  }

  public formatSiarStationLabel(station: SiarStation): string {
    if (!station) return '';
    const id =
      station.id !== undefined && station.id !== null ? String(station.id) : '';
    const name = station.name ? String(station.name) : '';
    if (id && name) {
      return `${id} - ${name}`;
    }
    return id || name;
  }

  private ensureAemetProvincesLoaded() {
    if (this.aemetProvinces.length) {
      return of(this.aemetProvinces);
    }
    return this.aemetStationsService.getProvinces().pipe(
      map(provinces => {
        this.aemetProvinces = provinces || [];
        return this.aemetProvinces;
      }),
      catchError(() => {
        this.aemetProvinces = [];
        return of([] as string[]);
      })
    );
  }

  private ensureSiarProvincesLoaded() {
    if (this.siarProvinces.length) {
      return of(this.siarProvinces);
    }
    return this.siarStationsService.getProvinces().pipe(
      map(provinces => {
        this.siarProvinces = provinces || [];
        return this.siarProvinces;
      }),
      catchError(() => {
        this.siarProvinces = [];
        return of([] as SiarProvince[]);
      })
    );
  }

  private loadAemetStations(
    magnitude: PestMagnitudeKey,
    province: string
  ): void {
    this.aemetStationsLoading[magnitude] = true;
    this.aemetStationsService
      .getStationsByProvince(province)
      .pipe(
        take(1),
        finalize(() => {
          this.aemetStationsLoading[magnitude] = false;
        }),
        catchError(() => of([] as AemetStation[]))
      )
      .subscribe(stations => {
        this.aemetStationsByMagnitude[magnitude] = stations || [];
      });
  }

  private loadSiarStations(
    magnitude: PestMagnitudeKey,
    provinceId: string | number
  ): void {
    this.siarStationsLoading[magnitude] = true;
    this.siarStationsService
      .getStationsByProvince(provinceId)
      .pipe(
        take(1),
        finalize(() => {
          this.siarStationsLoading[magnitude] = false;
        }),
        catchError(() => of([] as SiarStation[]))
      )
      .subscribe(stations => {
        this.siarStationsByMagnitude[magnitude] = stations || [];
      });
  }

  private ensureStationSelections(): void {
    (['temperature', 'humidity', 'precipitation', 'wind'] as PestMagnitudeKey[])
      .forEach(magnitude => {
        const source = this.getSourceValue(magnitude);
        if (source === 'AEMET') {
          const stationId = this.getStationValue(magnitude, 'aemet');
          if (stationId) {
            this.ensureAemetStationSelection(magnitude, stationId);
          } else {
            this.ensureAemetProvincesLoaded().pipe(take(1)).subscribe();
          }
        }
        if (source === 'SIAR') {
          const stationId = this.getStationValue(magnitude, 'siar');
          if (stationId) {
            this.ensureSiarStationSelection(magnitude, stationId);
          } else {
            this.ensureSiarProvincesLoaded().pipe(take(1)).subscribe();
          }
        }
      });
  }

  private ensureAemetStationSelection(
    magnitude: PestMagnitudeKey,
    stationId: string
  ): void {
    if (!stationId) return;
    this.ensureAemetProvincesLoaded()
      .pipe(
        take(1),
        switchMap(provinces =>
          forkJoin(
            provinces.map(province =>
              this.aemetStationsService.getStationsByProvince(province).pipe(
                map(stations => ({ province, stations }))
              )
            )
          )
        ),
        take(1),
        catchError(() => of([] as Array<{ province: string; stations: AemetStation[] }>))
      )
      .subscribe(entries => {
        for (const entry of entries) {
          const match = entry.stations.find(
            st => String(st?.idema) === String(stationId)
          );
          if (match) {
            this.aemetProvinceSelection[magnitude] = entry.province;
            this.aemetStationsByMagnitude[magnitude] = entry.stations;
            return;
          }
        }
      });
  }

  private ensureSiarStationSelection(
    magnitude: PestMagnitudeKey,
    stationId: string
  ): void {
    if (!stationId) return;
    this.ensureSiarProvincesLoaded()
      .pipe(
        take(1),
        switchMap(provinces =>
          forkJoin(
            provinces.map(province =>
              this.siarStationsService.getStationsByProvince(province.id).pipe(
                map(stations => ({ province, stations }))
              )
            )
          )
        ),
        take(1),
        catchError(() => of([] as Array<{ province: SiarProvince; stations: SiarStation[] }>))
      )
      .subscribe(entries => {
        for (const entry of entries) {
          const match = entry.stations.find(
            st => String(st?.id) === String(stationId)
          );
          if (match) {
            this.siarProvinceSelection[magnitude] = entry.province.id;
            this.siarStationsByMagnitude[magnitude] = entry.stations;
            return;
          }
        }
      });
  }

  private getStationValue(
    magnitude: PestMagnitudeKey,
    kind: 'aemet' | 'siar'
  ): string | null {
    if (magnitude === 'temperature') {
      return kind === 'aemet'
        ? this.magnitudeConfig.temperatureAemetStationId
        : this.magnitudeConfig.temperatureSiarStationId;
    }
    if (magnitude === 'humidity') {
      return kind === 'aemet'
        ? this.magnitudeConfig.humidityAemetStationId
        : this.magnitudeConfig.humiditySiarStationId;
    }
    if (magnitude === 'precipitation') {
      return kind === 'aemet'
        ? this.magnitudeConfig.precipitationAemetStationId
        : this.magnitudeConfig.precipitationSiarStationId;
    }
    return kind === 'aemet'
      ? this.magnitudeConfig.windAemetStationId
      : this.magnitudeConfig.windSiarStationId;
  }

  private isSensorSourceValue(
    magnitude: PestMagnitudeKey,
    source: string | null
  ): boolean {
    if (source === 'SENSOR_FINCA') return true;
    if (magnitude === 'precipitation' && source === 'PLUVIOMETER') return true;
    return false;
  }

  private buildEmptyMagnitudeConfig(): PestMagnitudeConfigForm {
    return {
      temperatureSource: null,
      humiditySource: null,
      precipitationSource: null,
      windSource: null,
      temperatureSensorId: null,
      humiditySensorId: null,
      precipitationSensorId: null,
      windSensorId: null,
      temperatureAemetStationId: null,
      temperatureSiarStationId: null,
      humidityAemetStationId: null,
      humiditySiarStationId: null,
      precipitationAemetStationId: null,
      precipitationSiarStationId: null,
      windAemetStationId: null,
      windSiarStationId: null
    };
  }

  private buildMagnitudePayload(): PestMagnitudeConfigForm {
    return {
      temperatureSource: this.magnitudeConfig.temperatureSource || null,
      humiditySource: this.magnitudeConfig.humiditySource || null,
      precipitationSource: this.magnitudeConfig.precipitationSource || null,
      windSource: this.magnitudeConfig.windSource || null,
      temperatureSensorId: this.normalizeId(this.magnitudeConfig.temperatureSensorId),
      humiditySensorId: this.normalizeId(this.magnitudeConfig.humiditySensorId),
      precipitationSensorId: this.normalizeId(
        this.magnitudeConfig.precipitationSensorId
      ),
      windSensorId: this.normalizeId(this.magnitudeConfig.windSensorId),
      temperatureAemetStationId: this.normalizeStation(
        this.magnitudeConfig.temperatureAemetStationId
      ),
      temperatureSiarStationId: this.normalizeStation(
        this.magnitudeConfig.temperatureSiarStationId
      ),
      humidityAemetStationId: this.normalizeStation(
        this.magnitudeConfig.humidityAemetStationId
      ),
      humiditySiarStationId: this.normalizeStation(
        this.magnitudeConfig.humiditySiarStationId
      ),
      precipitationAemetStationId: this.normalizeStation(
        this.magnitudeConfig.precipitationAemetStationId
      ),
      precipitationSiarStationId: this.normalizeStation(
        this.magnitudeConfig.precipitationSiarStationId
      ),
      windAemetStationId: this.normalizeStation(
        this.magnitudeConfig.windAemetStationId
      ),
      windSiarStationId: this.normalizeStation(
        this.magnitudeConfig.windSiarStationId
      )
    };
  }

  private normalizeId(value: any): number | null {
    if (value === null || value === undefined || value === '') return null;
    const num = Number(value);
    return Number.isFinite(num) ? num : null;
  }

  private normalizeStation(value: any): string | null {
    if (value === null || value === undefined || value === '') return null;
    return String(value);
  }

  private getSourceValue(magnitude: PestMagnitudeKey): string | null {
    switch (magnitude) {
      case 'temperature':
        return this.magnitudeConfig.temperatureSource;
      case 'humidity':
        return this.magnitudeConfig.humiditySource;
      case 'precipitation':
        return this.magnitudeConfig.precipitationSource;
      default:
        return this.magnitudeConfig.windSource;
    }
  }

  private setSensorValue(magnitude: PestMagnitudeKey, value: number | null): void {
    switch (magnitude) {
      case 'temperature':
        this.magnitudeConfig.temperatureSensorId = value;
        return;
      case 'humidity':
        this.magnitudeConfig.humiditySensorId = value;
        return;
      case 'precipitation':
        this.magnitudeConfig.precipitationSensorId = value;
        return;
      default:
        this.magnitudeConfig.windSensorId = value;
    }
  }

  private setStationValue(
    magnitude: PestMagnitudeKey,
    kind: 'aemet' | 'siar',
    value: string | null
  ): void {
    if (magnitude === 'temperature') {
      if (kind === 'aemet') {
        this.magnitudeConfig.temperatureAemetStationId = value;
      } else {
        this.magnitudeConfig.temperatureSiarStationId = value;
      }
      return;
    }
    if (magnitude === 'humidity') {
      if (kind === 'aemet') {
        this.magnitudeConfig.humidityAemetStationId = value;
      } else {
        this.magnitudeConfig.humiditySiarStationId = value;
      }
      return;
    }
    if (magnitude === 'precipitation') {
      if (kind === 'aemet') {
        this.magnitudeConfig.precipitationAemetStationId = value;
      } else {
        this.magnitudeConfig.precipitationSiarStationId = value;
      }
      return;
    }
    if (kind === 'aemet') {
      this.magnitudeConfig.windAemetStationId = value;
    } else {
      this.magnitudeConfig.windSiarStationId = value;
    }
  }

  private resetStationLists(): void {
    (['temperature', 'humidity', 'precipitation', 'wind'] as PestMagnitudeKey[])
      .forEach(magnitude => {
        this.aemetProvinceSelection[magnitude] = null;
        this.siarProvinceSelection[magnitude] = null;
        this.aemetStationsByMagnitude[magnitude] = [];
        this.siarStationsByMagnitude[magnitude] = [];
      });
  }

  private getSensorTypesForMagnitude(magnitude: PestMagnitudeKey): string[] {
    switch (magnitude) {
      case 'temperature':
      case 'humidity':
        return ['TE'];
      case 'precipitation':
        return ['PL'];
      default:
        return ['AN'];
    }
  }

  private getSensorType(sensor: any): string {
    return String(sensor?.sensorType || '').toUpperCase();
  }

  private getSensorLabel(sensor: any): string {
    const moduleId =
      this.getModuleId(sensor?.idModule) || this.getModuleId(sensor?.module);
    const moduleName =
      this.moduleMap.get(moduleId)?.name ||
      (moduleId
        ? this.translate.instant('PEST_MODELS.MODULE_FALLBACK', {
            id: moduleId
          })
        : '');
    const sensorName = sensor?.name || sensor?.alias || '';
    const typeLabel = sensor?.sensorType || '';
    if (sensorName) {
      return moduleName ? `${moduleName} · ${sensorName}` : sensorName;
    }
    const suffix = sensor?.idSensor ? `#${sensor.idSensor}` : '';
    return [moduleName, typeLabel, suffix].filter(Boolean).join(' ');
  }

  public trackByModelCode(index: number, model: PestModelCatalogModel): string {
    return model?.code || String(index);
  }

  public trackByInsightKey(index: number, insight: PestModelInsight): string {
    return (
      insight?.fingerprint ||
      insight?.insightKey ||
      String(index)
    );
  }

  public trackByModuleId(index: number, mod: any): number | string {
    return mod?.idModule ?? index;
  }

  public trackByOptionValue(index: number, option: { value?: string }): string {
    return option?.value || String(index);
  }

  public trackByString(index: number, value: string): string {
    return value || String(index);
  }

  public trackByAemetStation(index: number, station: AemetStation): string {
    return station?.idema || String(index);
  }

  public trackBySiarProvince(index: number, province: SiarProvince): string {
    const id =
      province?.id !== undefined && province?.id !== null
        ? String(province.id)
        : '';
    return id || String(index);
  }

  public trackBySiarStation(index: number, station: SiarStation): string {
    const id =
      station?.id !== undefined && station?.id !== null
        ? String(station.id)
        : '';
    return id || String(index);
  }

  public trackBySensorId(index: number, option: SensorOptionItem): number | string {
    return option?.id ?? index;
  }

  public trackBySubscriptionValue(
    index: number,
    option: { value?: string }
  ): string {
    return option?.value || String(index);
  }

  private getModuleId(input: any): number {
    if (!input) return 0;
    if (typeof input === 'number') return input;
    if (typeof input === 'string') return Number(input) || 0;
    return (
      Number(
        input?.idModule ||
          input?.moduleId ||
          input?.id ||
          input?.idModulo
      ) || 0
    );
  }

  public togglePayload(code: string): void {
    if (this.payloadExpanded.has(code)) {
      this.payloadExpanded.delete(code);
    } else {
      this.payloadExpanded.add(code);
    }
  }

  public isPayloadOpen(code: string): boolean {
    return this.payloadExpanded.has(code);
  }

  public formatDate(value: any): string {
    if (!value) return '-';
    const d = this.parseApiDate(value);
    if (Number.isNaN(d.getTime())) {
      return String(value);
    }
    return new Intl.DateTimeFormat(undefined, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    }).format(d);
  }

  private parseApiDate(value: any): Date {
    if (value instanceof Date) {
      return value;
    }
    const text = String(value || '').trim();
    const compact = text.match(
      /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})([+-]\d{2}:\d{2}|Z)?$/
    );
    if (compact) {
      const iso = `${compact[1]}-${compact[2]}-${compact[3]}T${compact[4]}:${compact[5]}:${compact[6]}${compact[7] || 'Z'}`;
      return new Date(iso);
    }
    return new Date(text);
  }

  private formatDateOnly(value: any): string {
    const d = this.parseApiDate(value);
    if (Number.isNaN(d.getTime())) {
      return '';
    }
    return new Intl.DateTimeFormat(undefined, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).format(d);
  }

  public formatContext(context: any): string {
    if (context === null || context === undefined) {
      return '';
    }
    if (typeof context === 'string') {
      return context;
    }
    if (typeof context === 'object') {
      const reason =
        (context as any).reason ||
        (context as any).message ||
        (context as any).status ||
        '';
      if (reason) {
        return String(reason);
      }
    }
    try {
      return JSON.stringify(context, null, 2);
    } catch (e) {
      return String(context);
    }
  }

  public formatContextSummary(context: any): string {
    if (context === null || context === undefined) {
      return '-';
    }
    if (typeof context === 'string') {
      return context;
    }
    if (typeof context !== 'object') {
      return String(context);
    }
    const entries = Object.entries(context);
    if (!entries.length) return '-';
    const parts = entries.slice(0, 3).map(([key, value]) => {
      if (value === null || value === undefined) {
        return `${key}: -`;
      }
      if (typeof value === 'string' || typeof value === 'number') {
        return `${key}: ${value}`;
      }
      return `${key}: [obj]`;
    });
    return parts.join(' · ');
  }

  public hasContextDetails(context: any): boolean {
    return context !== null && context !== undefined && typeof context === 'object';
  }

  public formatQuality(quality: any): string {
    if (quality === null || quality === undefined) return '-';
    if (typeof quality === 'string') return quality;
    try {
      return JSON.stringify(quality);
    } catch (e) {
      return String(quality);
    }
  }

  public extractSuspensionReason(state: PestModelState | null | undefined): string {
    if (!state) return '';
    const reason =
      this.getFirstLine(state.lastRunSummary) ||
      this.formatContextFirstLine(state.lastContext);
    return reason || '';
  }

  public isSuspendedState(state: PestModelState | null | undefined): boolean {
    if (!state) return false;
    const status = String(state.status || '').toUpperCase();
    const runStatus = String(state.lastRunStatus || '').toUpperCase();
    return status === 'SUSPENDED' || runStatus === 'SUSPENDED';
  }

  public hasExecution(state: PestModelState | null | undefined): boolean {
    if (!state) return false;
    return !!state.lastRunAt;
  }

  public shouldShowPausedAfterSubscription(model: PestModelCatalogModel): boolean {
    const state = model?.fincaModel;
    if (!state) return false;
    if (!state.lastRunAt) return false;
    if (this.isSuspendedState(state)) return false;
    return model?.hasAnyActiveSubscriptions === false;
  }

  public shouldRenderDebug(): boolean {
    return this.isAdminRole() && this.debugEnabled === true;
  }

  public onDebugToggle(enabled: boolean): void {
    if (!this.isAdminRole()) {
      this.debugEnabled = false;
      return;
    }
    this.debugEnabled = enabled === true;
    this.saveDebugPreference();
  }

  public getDebugMotorSubscription(model: PestModelCatalogModel): string {
    if (model?.hasAnyActiveSubscriptions === true) return 'activa';
    if (model?.hasAnyActiveSubscriptions === false) return 'inactiva';
    return 'desconocida';
  }

  public getDebugMotorReason(model: PestModelCatalogModel): string {
    const state = model?.fincaModel;
    const fromSummary = this.getFirstLine(state?.lastRunSummary);
    if (fromSummary) return fromSummary;
    const fromContext = this.formatContextFirstLine(state?.lastContext);
    if (fromContext && fromContext !== '-') return fromContext;
    const fromModel = this.getFirstLine(model?.reason);
    return fromModel || '-';
  }

  public getDebugMotorApplicability(model: PestModelCatalogModel): string {
    if (model?.applicable === true) return 'aplicable';
    if (model?.applicable === false) return 'no aplicable';
    return 'desconocida';
  }

  public getDebugExecutionStatus(model: PestModelCatalogModel): string {
    const state = model?.fincaModel;
    const status = String(state?.status || '-');
    const runStatus = String(state?.lastRunStatus || '-');
    return `${status} / ${runStatus}`;
  }

  public getDebugExecutionCause(model: PestModelCatalogModel): string {
    return this.getDebugMainCause(model);
  }

  public getDebugExecutionPlan(model: PestModelCatalogModel): string {
    const state = model?.fincaModel;
    if (!state) return 'sin estado';
    if (this.isSuspendedState(state)) return 'bloqueado';
    if (state.nextRunAt) return 'programado';
    return 'sin programación';
  }

  public getDebugRequiredMagnitudeStatuses(
    model: PestModelCatalogModel
  ): Array<{ magnitude: string; state: 'OK' | 'DERIVED' | 'NO_DATA' }> {
    const required = Array.isArray(model?.requiredMagnitudes)
      ? model.requiredMagnitudes
      : [];
    const magnitudes = model?.fincaModel?.lastQuality?.magnitudes || {};
    return required.map(item => {
      const key = String(item || '');
      const raw =
        magnitudes?.[key] ??
        magnitudes?.[key.toLowerCase?.() || ''] ??
        magnitudes?.[key.toUpperCase?.() || ''];
      return {
        magnitude: key,
        state: this.normalizeMagnitudeQuality(raw)
      };
    });
  }

  public getDebugMainCause(model: PestModelCatalogModel): string {
    const state = model?.fincaModel;
    if (!this.shouldShowDebugMainCause(model)) {
      return 'OK';
    }
    if (this.isHourlyRequiredAndMissing(model)) {
      return 'HOURLY_REQUIRED';
    }
    if (this.hasMissingCriticalData(model)) {
      return 'MISSING_CRITICAL_DATA';
    }
    if (model?.hasAnyActiveSubscriptions === false) {
      return 'NO_ACTIVE_SUBSCRIPTION';
    }
    if (state && !state.nextRunAt) {
      return 'NOT_SCHEDULED';
    }
    return 'OK';
  }

  public getDebugMainCauseLabel(model: PestModelCatalogModel): string {
    const code = this.getDebugMainCause(model);
    switch (code) {
      case 'HOURLY_REQUIRED':
        return 'HOURLY requerido (no hay datos horarios)';
      case 'MISSING_CRITICAL_DATA':
        return 'Faltan magnitudes criticas';
      case 'NOT_SCHEDULED':
        return 'Sin programacion';
      case 'NO_ACTIVE_SUBSCRIPTION':
        return 'Sin suscripcion activa';
      default:
        return code;
    }
  }

  public shouldShowDebugMainCause(model: PestModelCatalogModel): boolean {
    const status = String(model?.fincaModel?.status || '').toUpperCase();
    return status === 'SUSPENDED';
  }

  private hasMissingCriticalData(model: PestModelCatalogModel): boolean {
    return this.getDebugRequiredMagnitudeStatuses(model).some(
      item => item.state === 'NO_DATA'
    );
  }

  private isHourlyRequiredAndMissing(model: PestModelCatalogModel): boolean {
    if (String(model?.idealResolution || '').toUpperCase() !== 'HOURLY') {
      return false;
    }
    const state = model?.fincaModel;
    const q = state?.lastQuality;
    const sourceText = [
      this.getFirstLine(state?.lastRunSummary),
      this.getFirstLine(model?.reason),
      this.formatContextFirstLine(state?.lastContext),
      q ? JSON.stringify(q) : ''
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    const hasHourlySignal =
      sourceText.includes('hourly') ||
      sourceText.includes('horaria') ||
      sourceText.includes('horario');
    const hasMissingSignal =
      sourceText.includes('missing') ||
      sourceText.includes('required') ||
      sourceText.includes('no data') ||
      sourceText.includes('none') ||
      sourceText.includes('sin datos') ||
      sourceText.includes('faltan');
    return hasHourlySignal && hasMissingSignal;
  }

  private normalizeMagnitudeQuality(raw: any): 'OK' | 'DERIVED' | 'NO_DATA' {
    if (raw === null || raw === undefined || raw === '') {
      return 'NO_DATA';
    }
    if (typeof raw === 'string') {
      const text = raw.toUpperCase();
      if (text.includes('DERIVED')) return 'DERIVED';
      if (text.includes('NO_DATA') || text.includes('MISSING')) return 'NO_DATA';
      if (text.includes('OK')) return 'OK';
      return 'OK';
    }
    if (typeof raw === 'object') {
      const candidate =
        raw.status ?? raw.quality ?? raw.state ?? raw.value ?? raw.result ?? '';
      return this.normalizeMagnitudeQuality(candidate);
    }
    return 'OK';
  }

  public formatContextFirstLine(context: any): string {
    if (context === null || context === undefined) {
      return '-';
    }
    if (typeof context === 'string') {
      return this.getFirstLine(context) || '-';
    }
    if (typeof context !== 'object') {
      return String(context);
    }
    const entries = Object.entries(context);
    if (!entries.length) {
      return '-';
    }
    const [key, value] = entries[0];
    if (value === null || value === undefined) {
      return `${key}: -`;
    }
    if (typeof value === 'string' || typeof value === 'number') {
      return `${key}: ${value}`;
    }
    return `${key}: [obj]`;
  }

  public formatWindowSummary(value: any): string {
    if (!value || typeof value !== 'object') {
      return '-';
    }
    const fromRaw = (value as any).from ?? (value as any).start ?? null;
    const toRaw = (value as any).to ?? (value as any).end ?? null;
    if (!fromRaw || !toRaw) {
      return '-';
    }
    const from = this.formatDateOnly(fromRaw);
    const to = this.formatDateOnly(toRaw);
    if (!from || !to) {
      return '-';
    }
    return `${from} → ${to}`;
  }

  public getDdAccumulatedSummary(model: PestModelCatalogModel): string | null {
    const code = String(model?.code || '').toUpperCase();
    if (!code.includes('DD')) {
      return null;
    }
    const summary = model?.fincaModel?.lastRunSummary;
    if (!summary) {
      return null;
    }
    return this.findDdAccumulated(summary);
  }

  public technicalToggleKey(code: string): string {
    return `${code}-technical`;
  }

  public insightContextToggleKey(
    modelCode: string,
    insight: PestModelInsight,
    index: number
  ): string {
    const fingerprint = insight?.fingerprint || insight?.insightKey || index;
    return `${modelCode}-insight-${fingerprint}`;
  }

  private getFirstLine(value: any): string {
    if (value === null || value === undefined) {
      return '';
    }
    const text = String(value);
    const parts = text
      .split(/\r?\n/)
      .map(line => line.trim())
      .filter(Boolean);
    return parts[0] || '';
  }

  private findDdAccumulated(summary: any): string | null {
    const parsed = this.parseMaybeJson(summary);
    const fromObject = this.searchDdInObject(parsed);
    if (fromObject) {
      return fromObject;
    }
    const text = typeof parsed === 'string' ? parsed : JSON.stringify(parsed);
    const match = text.match(
      /(dd(?:[_\s-]*acum(?:ulado)?)?|degree(?:[_\s-]*days)?(?:[_\s-]*accumulated)?)\D*(-?\d+(?:[.,]\d+)?)/i
    );
    return match ? match[2] : null;
  }

  private parseMaybeJson(value: any): any {
    if (typeof value !== 'string') {
      return value;
    }
    const trimmed = value.trim();
    if (
      !trimmed ||
      ((!trimmed.startsWith('{') || !trimmed.endsWith('}')) &&
        (!trimmed.startsWith('[') || !trimmed.endsWith(']')))
    ) {
      return value;
    }
    try {
      return JSON.parse(trimmed);
    } catch (e) {
      return value;
    }
  }

  private searchDdInObject(input: any): string | null {
    const stack: any[] = [input];
    let steps = 0;
    while (stack.length && steps < 300) {
      steps += 1;
      const current = stack.pop();
      if (current === null || current === undefined) {
        continue;
      }
      if (Array.isArray(current)) {
        current.forEach(item => stack.push(item));
        continue;
      }
      if (typeof current !== 'object') {
        continue;
      }
      for (const [rawKey, rawValue] of Object.entries(current)) {
        const key = String(rawKey).toLowerCase();
        const isDdKey =
          key.includes('dd') ||
          key.includes('degree') ||
          key.includes('grado') ||
          key.includes('acum');
        if (
          isDdKey &&
          (typeof rawValue === 'number' ||
            (typeof rawValue === 'string' &&
              rawValue.trim() &&
              !Number.isNaN(Number(rawValue))))
        ) {
          return String(rawValue);
        }
        if (rawValue && typeof rawValue === 'object') {
          stack.push(rawValue);
        }
      }
    }
    return null;
  }

  public hasNoQuality(state: PestModelState | null | undefined): boolean {
    if (!state) return false;
    const q = state.lastQuality;
    if (q === null || q === undefined) return true;
    if (typeof q === 'string' && q.trim().toLowerCase() === 'none') return true;
    return false;
  }

  public safeInsights(insights: PestModelInsight[] | undefined): PestModelInsight[] {
    if (!Array.isArray(insights)) return [];
    return insights;
  }

  public contextToggleKey(code: string): string {
    return `${code}-context`;
  }

  public modelStatusClass(state: PestModelState | null | undefined): string {
    const status = (state?.status || '').toUpperCase();
    if (status === 'SUSPENDED') return 'status-chip danger';
    if (status === 'ACTIVE_RISK' || status === 'TRIGGERED') {
      return 'status-chip warning';
    }
    if (status === 'MONITORING') return 'status-chip info';
    return 'status-chip';
  }

  public getAgronomicStatusKey(
    state: PestModelState | null | undefined
  ): string {
    const status = String(state?.status || '').toUpperCase();
    switch (status) {
      case 'MONITORING':
        return 'PEST_MODELS.AGRONOMIC_STATUS.MONITORING';
      case 'TRIGGERED':
        return 'PEST_MODELS.AGRONOMIC_STATUS.TRIGGERED';
      case 'ACTIVE_RISK':
        return 'PEST_MODELS.AGRONOMIC_STATUS.ACTIVE_RISK';
      case 'RESOLVED':
        return 'PEST_MODELS.AGRONOMIC_STATUS.RESOLVED';
      case 'COMPLETED':
        return 'PEST_MODELS.AGRONOMIC_STATUS.COMPLETED';
      case 'DORMANT':
        return 'PEST_MODELS.AGRONOMIC_STATUS.DORMANT';
      case 'SUSPENDED':
        return 'PEST_MODELS.AGRONOMIC_STATUS.SUSPENDED';
      default:
        return 'PEST_MODELS.NO_STATE_SHORT';
    }
  }

  public getCatalogName(model: PestModelCatalogModel): string {
    const key = `pestModels.model.${model.code}.name`;
    const translated = this.translate.instant(key);
    if (translated && translated !== key) return translated;
    return model.code;
  }

  public getCatalogDescription(model: PestModelCatalogModel): string {
    const key = `pestModels.model.${model.code}.description`;
    const translated = this.translate.instant(key);
    if (translated && translated !== key) return translated;
    return '';
  }

  public getCatalogType(type: string | undefined): string {
    if (!type) return '';
    const key = `PEST_MODEL.TYPE.${type}`;
    const translated = this.translate.instant(key);
    return translated && translated !== key ? translated : type;
  }

  public getCatalogStatus(status: string | undefined): string {
    if (!status) return '';
    const key = `PEST_MODEL.CATALOG_STATUS.${status.toUpperCase()}`;
    const translated = this.translate.instant(key);
    return translated && translated !== key ? translated : status;
  }

  public getFrequencyLabel(value: string | undefined): string {
    if (!value) return '';
    const key = `PEST_MODEL.FREQUENCY.${value}`;
    const translated = this.translate.instant(key);
    return translated && translated !== key ? translated : value;
  }

  public getResolutionLabel(value: string | undefined): string {
    if (!value) return '';
    const key = `PEST_MODEL.RESOLUTION.${value}`;
    const translated = this.translate.instant(key);
    return translated && translated !== key ? translated : value;
  }

  public isInitialState(model: PestModelCatalogModel): boolean {
    const state = model?.fincaModel;
    if (!state) return false;
    return state.lastRunAt === null && state.lastComputedWindow === null;
  }

  public openSubscribeDialog(model: PestModelCatalogModel): void {
    this.subscriptionModel = model;
    this.subscriptionLevel = this.getSubscriptionLevel(model);
    if (!this.subscriptionDialog) return;
    this.subscriptionDialogRef = this.dialog.open(this.subscriptionDialog, {
      width: '420px',
      autoFocus: false,
      restoreFocus: true
    });
  }

  public closeSubscriptionDialog(): void {
    this.subscriptionDialogRef?.close();
  }

  public confirmSubscription(): void {
    if (!this.subscriptionModel || !this.fincaId) return;
    const payload = this.buildSubscriptionPayload(
      this.fincaId,
      this.subscriptionModel.code,
      this.subscriptionLevel
    );

    this.pestModelsSubscription.upsertSubscription(payload).subscribe({
      next: () => {
        this.snackBar.open(
          this.translate.instant('PEST_MODELS.SUBSCRIBE_SUCCESS'),
          this.translate.instant('COMMON.CLOSE'),
          { duration: 3500 }
        );
        this.closeSubscriptionDialog();
        this.refresh();
      },
      error: () => {
        this.snackBar.open(
          this.translate.instant('PEST_MODELS.SUBSCRIBE_ERROR'),
          this.translate.instant('COMMON.CLOSE'),
          { duration: 4000 }
        );
      }
    });
  }

  public unsubscribe(model: PestModelCatalogModel): void {
    if (!this.fincaId) return;
    this.pestModelsSubscription
      .disableSubscription(this.fincaId, model.code)
      .subscribe({
        next: () => {
          this.snackBar.open(
            this.translate.instant('PEST_MODELS.UNSUBSCRIBE_SUCCESS'),
            this.translate.instant('COMMON.CLOSE'),
            { duration: 3500 }
          );
          this.refresh();
        },
        error: () => {
          this.snackBar.open(
            this.translate.instant('PEST_MODELS.UNSUBSCRIBE_ERROR'),
            this.translate.instant('COMMON.CLOSE'),
            { duration: 4000 }
          );
        }
      });
  }

  public getSubscriptionLevelLabel(model: PestModelCatalogModel): string {
    const level = this.getSubscriptionLevel(model);
    const option = this.subscriptionLevels.find(item => item.value === level);
    if (!option) return level;
    return this.translate.instant(option.labelKey);
  }

  private getSubscriptionLevel(model: PestModelCatalogModel): string {
    const sub = model.userSubscription;
    const notifyInfo = sub?.notifyInfo === true;
    const notifyWarning = sub?.notifyWarning === true;
    const notifyAlert = sub?.notifyAlert === true;
    if (notifyInfo && notifyWarning && notifyAlert) return 'ALL';
    if (!notifyInfo && notifyWarning && notifyAlert) return 'WARN_ALERT';
    if (!notifyInfo && !notifyWarning && notifyAlert) return 'ALERT_ONLY';
    if (!notifyInfo && !notifyWarning && !notifyAlert) return 'NONE';
    return 'WARN_ALERT';
  }

  private buildSubscriptionPayload(
    idFinca: number,
    modelCode: string,
    level: string
  ) {
    const payload: any = {
      idFinca,
      modelCode,
      isEnabled: true
    };

    if (level === 'ALL') {
      payload.notifyInfo = true;
      payload.notifyWarning = true;
      payload.notifyAlert = true;
    } else if (level === 'WARN_ALERT') {
      payload.notifyInfo = false;
      payload.notifyWarning = true;
      payload.notifyAlert = true;
    } else if (level === 'ALERT_ONLY') {
      payload.notifyInfo = false;
      payload.notifyWarning = false;
      payload.notifyAlert = true;
    } else {
      payload.notifyInfo = false;
      payload.notifyWarning = false;
      payload.notifyAlert = false;
    }

    return payload;
  }

  private isAdminRole(): boolean {
    return this.roleId === 1;
  }

  private isNotAdmin(): boolean {
    return !this.isAdminRole();
  }

  private getDebugStorageKey(): string {
    const user = this.userService?.usuario || {};
    const rawId = user?.idUser ?? user?.id ?? user?.email ?? 'anon';
    return `${this.debugStorageKeyBase}.${String(rawId)}`;
  }

  private loadDebugPreference(): void {
    if (!this.isAdminRole()) {
      this.debugEnabled = false;
      return;
    }
    try {
      const raw = localStorage.getItem(this.getDebugStorageKey());
      this.debugEnabled = raw === 'true';
    } catch (e) {
      this.debugEnabled = false;
    }
  }

  private saveDebugPreference(): void {
    if (!this.isAdminRole()) {
      return;
    }
    try {
      localStorage.setItem(this.getDebugStorageKey(), this.debugEnabled ? 'true' : 'false');
    } catch (e) {}
  }
}
